import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  Button,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  createFilterOptions,
} from "@mui/material";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import DynamicFields from "../DynamicFields";
import { toActionValue } from "../../utils/chatbotSlotActions";
import { VARIAVEIS_CONTEXTO } from "./chatbotContextVariables";
import { validateCustomContextKey } from "./chatbotReservedContextKeys";
import ChatbotFlowPathLinks from "./ChatbotFlowPathLinks";
import { sortNavigationCatalogActions } from "../../utils/chatbotNavigationActions";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const GROUP_ORDER = [
  "lead",
  "core",
  "automation",
  "context",
  "ticket",
  "customer",
  "ecommerce",
  "integration",
  "navigation",
  "ai",
];

const GROUP_LABELS = {
  core: "Fluxo",
  lead: "Solicitação",
  navigation: "Navegação no fluxo",
  automation: "Regras de automação",
  context: "Contexto",
  ticket: "Passagens",
  customer: "Cliente",
  ecommerce: "E-commerce",
  integration: "Integrações",
  ai: "IA (legado)",
};

const filterOptions = createFilterOptions({
  limit: 60,
  stringify: (option) =>
    [option?.searchText, option?.label, option?.shortLabel, option?.slotPath, option?.description, option?.helpText]
      .filter(Boolean)
      .join(" "),
});

function actionGroup(action) {
  if (!action) return "core";
  if (action.group) return action.group;
  if (Number(action.tipo_acao) === 2) return "navigation";
  if (Number(action.tipo_acao) === 3) return "automation";
  if (Number(action.tipo_acao) === 4) return "lead";
  if (Number(action.tipo_acao) === 5) return action.group || "context";
  return action.domain || "core";
}

function isWebchatInviteAction(item) {
  return Number(item?.tipo_acao) === 1 && Number(item?.id_acao) === 91;
}

function isParametricAction(item) {
  const tipo = Number(item?.tipo_acao) || 0;
  return tipo === 4 || tipo === 5 || isWebchatInviteAction(item);
}

function ParametricActionFields({ item, idx, onUpdate }) {
  const tipo = Number(item.tipo_acao) || 0;
  const id = Number(item.id_acao) || 0;

  if (tipo === 5 && id === 1) {
    const texto = item.values?.texto || "";
    const insertVariable = (token) => {
      onUpdate(idx, { values: { texto: `${texto}${token}` } });
    };
    return (
      <Stack spacing={1}>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={4}
          label="Texto da anotação"
          placeholder="Ex.: Cliente solicitou remarcação. Mensagem: $mensagem"
          value={texto}
          onChange={(e) => onUpdate(idx, { values: { texto: e.target.value } })}
          helperText="Variáveis: use o mesmo nome da chave salva (sem acento), ex.: $tema_da_duvida ou [tema_da_duvida]"
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {VARIAVEIS_CONTEXTO.slice(0, 8).map((v) => (
            <Chip
              key={v.name}
              size="small"
              variant="outlined"
              label={v.name}
              onClick={() => insertVariable(v.name)}
            />
          ))}
        </Box>
      </Stack>
    );
  }

  if (tipo === 5 && id === 2) {
    const chave = item.values?.chave || "";
    const validation = chave.trim() ? validateCustomContextKey(chave) : { ok: true, message: "" };
    const varToken = validation.ok && validation.key ? `$${validation.key}` : "";
    return (
      <Stack spacing={1}>
        <TextField
          size="small"
          fullWidth
          label="Chave do contexto"
          placeholder="Ex.: mensagem_cliente"
          value={chave}
          onChange={(e) => onUpdate(idx, { values: { chave: e.target.value } })}
          error={!validation.ok}
          helperText={
            validation.ok
              ? varToken
                ? `Salva a última mensagem do cliente. Use na anotação: ${varToken} ou [${validation.key}]`
                : "Salva a última mensagem do cliente nesta chave (sem acentos, use _)."
              : validation.message
          }
        />
        {varToken ? (
          <Chip
            size="small"
            variant="outlined"
            label={varToken}
            onClick={() => navigator.clipboard?.writeText?.(varToken)}
          />
        ) : null}
      </Stack>
    );
  }

  if (tipo === 1 && id === 91) {
    const buttonLabel = item.values?.button_label || "";
    return (
      <TextField
        size="small"
        fullWidth
        label="Texto do botão (máx. 20)"
        placeholder="Continuar"
        value={buttonLabel}
        onChange={(e) => onUpdate(idx, { values: { button_label: e.target.value.slice(0, 20) } })}
        inputProps={{ maxLength: 20 }}
        helperText="Se vazio, o botão mostra Continuar."
      />
    );
  }

  return null;
}

function ActionUsageHelp({ catalog }) {
  const usage = catalog?.usage;
  const helpText = String(catalog?.helpText || "").trim();
  if (!usage && !helpText) return null;

  const list = (items) =>
    (Array.isArray(items) ? items : []).filter((x) => String(x || "").trim());

  return (
    <Alert severity="info" sx={{ py: 0.75 }}>
      <Stack spacing={0.75}>
        {helpText ? (
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            {helpText}
          </Typography>
        ) : null}
        {list(usage?.requirements).length ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Requisitos
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
              {list(usage.requirements).map((line) => (
                <Typography key={line} component="li" variant="caption" sx={{ display: "list-item", lineHeight: 1.4 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}
        {list(usage?.nextSteps).length ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Nos próximos passos
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.25 }}>
              {list(usage.nextSteps).map((line) => (
                <Typography key={line} component="li" variant="caption" sx={{ display: "list-item", lineHeight: 1.4 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null}
        {list(usage?.reads).length ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Variáveis que a ação lê
            </Typography>
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>
              {list(usage.reads).join(" · ")}
            </Typography>
          </Box>
        ) : null}
        {list(usage?.writes).length ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Variáveis gravadas no contexto (use na mensagem ou no agente)
            </Typography>
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>
              {list(usage.writes).join(" · ")}
            </Typography>
          </Box>
        ) : null}
        {usage?.messageHint ? (
          <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
            {usage.messageHint}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  );
}

function summarizeParametros(labels = {}, values = {}, item = null) {
  const tipo = Number(item?.tipo_acao) || 0;
  const id = Number(item?.id_acao) || 0;
  if (tipo === 5 && id === 1 && values?.texto) {
    const preview = String(values.texto).replace(/\s+/g, " ").trim();
    return preview.length > 60 ? `${preview.slice(0, 60)}…` : preview;
  }
  if (tipo === 5 && id === 2 && values?.chave) {
    return `chave: ${values.chave}`;
  }
  if (tipo === 1 && id === 91 && values?.button_label) {
    return `botão: ${values.button_label}`;
  }
  const transferLabel =
    labels?.transferirusuario ||
    labels?.transferirparaagente ||
    labels?.transferirresponsavel;
  if (transferLabel && String(transferLabel).trim()) {
    return `transferir: ${transferLabel}`;
  }
  const pairs = Object.entries(labels || {}).filter(([, v]) => v != null && String(v).trim() !== "");
  if (!pairs.length) return null;
  return pairs
    .slice(0, 2)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
    .join(" • ");
}

function getOptionLabel(option) {
  if (!option) return "";
  if (Number(option.tipo_acao) === 2) return option.shortLabel || option.label || "";
  return option.label || option.shortLabel || "";
}

function navigationGroupLabel(option) {
  if (Number(option?.tipo_acao) !== 2) {
    return GROUP_LABELS[actionGroup(option)] || actionGroup(option);
  }
  if (Number(option.navRank ?? 100) <= 2) {
    return "Navegação · neste ramo";
  }
  return "Navegação · outros passos";
}

function findCatalogAction(actions, item) {
  const value = item?.value || toActionValue(item?.tipo_acao, item?.id_acao);
  return (actions || []).find((a) => a.value === value) || null;
}

function actionTitle(catalog, item) {
  if (!item) return "Ação";
  const isNav = Number(item.tipo_acao) === 2;
  if (catalog) return isNav ? catalog.shortLabel || catalog.label : catalog.label;
  return `Ação ${item.tipo_acao}-${item.id_acao}`;
}

function renderActionOption(props, option) {
  const isNav = Number(option.tipo_acao) === 2;
  const isAutomation = Number(option.tipo_acao) === 3;
  return (
    <Box component="li" {...props} key={option.value}>
      <Box sx={{ minWidth: 0, py: 0.25 }}>
        <Typography variant="body2" sx={{ fontWeight: isNav || isAutomation ? 500 : 400 }}>
          {isAutomation ? `Regra: ${getOptionLabel(option)}` : getOptionLabel(option)}
        </Typography>
        {isNav && option.slotPath ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {option.slotPath}
            {option.slotId ? ` · passo #${option.slotId}` : option.id_acao ? ` · passo #${option.id_acao}` : ""}
          </Typography>
        ) : option.description ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {option.description}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function ActionSearchField({
  actions,
  isLoading,
  placeholder,
  onPick,
  categoryFilter = "all",
  autoFocus = false,
}) {
  const [inputValue, setInputValue] = useState("");
  const [picked, setPicked] = useState(null);

  const options = useMemo(() => {
    const list = actions || [];
    const filtered =
      categoryFilter === "all" ? list : list.filter((a) => actionGroup(a) === categoryFilter);
    return sortNavigationCatalogActions(filtered);
  }, [actions, categoryFilter]);

  const handleChange = (_, option) => {
    if (!option) return;
    onPick(option);
    setPicked(null);
    setInputValue("");
  };

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={isLoading}
      loading={isLoading}
      options={options}
      value={picked}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
      filterOptions={filterOptions}
      groupBy={(option) =>
        Number(option.tipo_acao) === 2
          ? navigationGroupLabel(option)
          : GROUP_LABELS[actionGroup(option)] || actionGroup(option)
      }
      noOptionsText={
        inputValue.trim()
          ? "Nenhuma ação encontrada — tente outro termo ou categoria"
          : "Digite para buscar entre as ações"
      }
      autoHighlight
      openOnFocus={Boolean(inputValue.trim())}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon fontSize="small" color="action" sx={{ ml: 0.5, mr: -0.5 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={renderActionOption}
      ListboxProps={{ sx: { maxHeight: 320 } }}
    />
  );
}

function AddActionDialog({ open, onClose, actions, isLoading, onPick }) {
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const groupCounts = useMemo(() => {
    const counts = new Map();
    for (const action of actions || []) {
      const g = actionGroup(action);
      counts.set(g, (counts.get(g) || 0) + 1);
    }
    return counts;
  }, [actions]);

  const suggested = useMemo(() => {
    const picks = [];
    const seen = new Set();
    for (const action of actions || []) {
      const g = actionGroup(action);
      if (g === "navigation") continue;
      if (seen.has(action.value)) continue;
      if (g === "lead" || g === "core" || g === "automation" || g === "context") {
        picks.push(action);
        seen.add(action.value);
      }
      if (picks.length >= 6) break;
    }
    return picks;
  }, [actions]);

  const handlePick = (catalogAction) => {
    onPick(catalogAction);
    onClose();
    setCategoryFilter("all");
    setShowFilters(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Adicionar ação</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <Stack spacing={2}>
          <ActionSearchField
            actions={actions}
            isLoading={isLoading}
            categoryFilter={categoryFilter}
            autoFocus
            placeholder="Buscar por nome, passo ou destino (ex.: alterar, tag, regra, origem)…"
            onPick={handlePick}
          />

          {suggested.length > 0 ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                Sugestões frequentes
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {suggested.map((a) => (
                  <Chip
                    key={a.value}
                    size="small"
                    variant="outlined"
                    label={getOptionLabel(a)}
                    onClick={() => handlePick(a)}
                  />
                ))}
              </Box>
            </Box>
          ) : null}

          <Box>
            <Button
              size="small"
              variant="text"
              sx={{ textTransform: "none", px: 0 }}
              onClick={() => setShowFilters((v) => !v)}
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Filtrar por categoria
              {categoryFilter !== "all"
                ? ` · ${GROUP_LABELS[categoryFilter] || categoryFilter}`
                : ""}
            </Button>
            <Collapse in={showFilters}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                <Chip
                  size="small"
                  label={`Todas (${actions.length})`}
                  color={categoryFilter === "all" ? "primary" : "default"}
                  variant={categoryFilter === "all" ? "filled" : "outlined"}
                  onClick={() => setCategoryFilter("all")}
                />
                {GROUP_ORDER.filter((g) => groupCounts.has(g)).map((groupKey) => (
                  <Chip
                    key={groupKey}
                    size="small"
                    label={`${GROUP_LABELS[groupKey] || groupKey} (${groupCounts.get(groupKey)})`}
                    color={categoryFilter === groupKey ? "primary" : "default"}
                    variant={categoryFilter === groupKey ? "filled" : "outlined"}
                    onClick={() => setCategoryFilter(groupKey)}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function ActionRow({
  item,
  idx,
  total,
  catalog,
  actions,
  isLoading,
  expanded,
  onToggleExpand,
  onMove,
  onRemove,
  onReplace,
  onUpdate,
  onNavigateToSlot,
  slots = [],
}) {
  const title = actionTitle(catalog, item);
  const resumo = summarizeParametros(item.labels, item.values, item);
  const parameterForm = catalog?.parameterForm || null;
  const isNav = Number(item.tipo_acao) === 2;
  const isAutomationRule = Number(item.tipo_acao) === 3;
  const isParametric = isParametricAction(item);
  const needsUrlapi = Number(item.tipo_acao) === 1 && [17, 55].includes(Number(item.id_acao));
  const [swapOpen, setSwapOpen] = useState(false);

  const targetSlotId = Number(catalog?.slotId || item.id_acao) || 0;

  const handleReplace = (catalogAction) => {
    const value = catalogAction?.value || toActionValue(catalogAction?.tipo_acao, catalogAction?.id_acao);
    const [t, a] = String(value).split("-").map(Number);
    onReplace(idx, {
      value,
      tipo_acao: t || 0,
      id_acao: a || 0,
      values: {},
      labels: {},
      urlapi: "",
    });
    setSwapOpen(false);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: "action.hover",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            minWidth: 20,
            color: "primary.main",
            pt: 0.25,
          }}
        >
          {idx + 1}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          {resumo ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {resumo}
            </Typography>
          ) : isNav && catalog?.slotPath ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: "break-word" }}>
              <ChatbotFlowPathLinks
                path={catalog.slotPath}
                slots={slots}
                targetSlotId={targetSlotId}
                onNavigate={onNavigateToSlot}
                variant="caption"
              />
              {targetSlotId ? ` · passo #${targetSlotId}` : ""}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0, alignItems: "flex-start" }}>
          {isNav && targetSlotId && onNavigateToSlot ? (
            <IconButton
              size="small"
              color="primary"
              onClick={() => onNavigateToSlot(targetSlotId)}
              aria-label={`Ver destino passo ${targetSlotId}`}
              title={`Ver destino (passo #${targetSlotId})`}
            >
              <OpenInNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : null}
          <IconButton size="small" disabled={idx === 0} onClick={() => onMove(idx, -1)} aria-label="Subir">
            <ArrowUpwardIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={idx === total - 1}
            onClick={() => onMove(idx, 1)}
            aria-label="Descer"
          >
            <ArrowDownwardIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onRemove(idx)} aria-label="Remover">
            <DeleteOutline sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      {catalog?.usage || catalog?.helpText ? (
        <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: "divider" }}>
          <ActionUsageHelp catalog={catalog} />
        </Box>
      ) : null}

      {(parameterForm || needsUrlapi || isNav || isParametric || isAutomationRule) && (
        <Box sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: "divider" }}>
          <Button
            size="small"
            variant="text"
            sx={{ textTransform: "none", minWidth: 0, px: 0.5 }}
            onClick={() => onToggleExpand(idx)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {expanded ? "Ocultar parâmetros" : "Configurar parâmetros"}
          </Button>
        </Box>
      )}

      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
          <Stack spacing={1.5}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={() => setSwapOpen(true)}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Trocar ação
            </Button>

            {isNav ? (
              <Alert severity="info" sx={{ py: 0.25 }}>
                Destino:{" "}
                <ChatbotFlowPathLinks
                  path={catalog?.slotPath}
                  slots={slots}
                  targetSlotId={targetSlotId}
                  onNavigate={onNavigateToSlot}
                  variant="body2"
                  emptyLabel={`passo #${targetSlotId || "?"}`}
                />
                {targetSlotId ? (
                  <>
                    {" "}
                    · passo #{targetSlotId}
                    {onNavigateToSlot ? (
                      <>
                        {" · "}
                        <Button
                          size="small"
                          variant="text"
                          sx={{ textTransform: "none", minWidth: 0, p: 0, verticalAlign: "baseline" }}
                          onClick={() => onNavigateToSlot(targetSlotId)}
                        >
                          Abrir no fluxo
                        </Button>
                      </>
                    ) : null}
                  </>
                ) : null}
              </Alert>
            ) : null}

            {isAutomationRule ? (
              <Alert severity="info" sx={{ py: 0.25 }}>
                {catalog?.description ||
                  "Executa a regra de automação vinculada. Edite a lógica em Regras de Automação — todos os passos que usam esta regra serão atualizados."}
              </Alert>
            ) : null}

            {parameterForm ? (
              <DynamicFields
                compact
                schema={parameterForm}
                values={item.values || {}}
                formState={{ condicoes_labels: item.labels || {} }}
                onChange={(name, value) => onUpdate(idx, { values: { [name]: value } })}
                onLabel={(name, label) => onUpdate(idx, { labels: { [name]: label } })}
                onValuesPatch={(patch) => onUpdate(idx, { values: patch })}
                onLabelsPatch={(patch) => onUpdate(idx, { labels: patch })}
              />
            ) : isParametric ? (
              <ParametricActionFields item={item} idx={idx} onUpdate={onUpdate} />
            ) : Number(item.tipo_acao) === 4 ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Carregando formulário da ação…
              </Alert>
            ) : null}

            {needsUrlapi ? (
              <TextField
                size="small"
                fullWidth
                label={Number(item.id_acao) === 55 ? "URL da planilha Google" : "URL da API"}
                value={item.urlapi || ""}
                onChange={(e) => onUpdate(idx, { urlapi: e.target.value })}
              />
            ) : null}
          </Stack>
        </Box>
      </Collapse>

      <AddActionDialog
        open={swapOpen}
        onClose={() => setSwapOpen(false)}
        actions={actions}
        isLoading={isLoading}
        onPick={handleReplace}
      />
    </Paper>
  );
}

function mergeActionPatch(item, patch) {
  const next = { ...item, ...patch };
  if (patch.values != null) {
    next.values = { ...(item.values || {}), ...patch.values };
  }
  if (patch.labels != null) {
    next.labels = { ...(item.labels || {}), ...patch.labels };
  }
  return next;
}

export default function ChatbotStepActionEditor({
  acoes = [],
  onAcoesChange,
  actions = [],
  isLoading = false,
  onNavigateToSlot,
  slots = [],
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const addAction = (catalogAction) => {
    const value = catalogAction?.value || toActionValue(catalogAction?.tipo_acao, catalogAction?.id_acao);
    const [t, a] = String(value).split("-").map(Number);
    const next = [
      ...(acoes || []),
      {
        value,
        tipo_acao: t || 0,
        id_acao: a || 0,
        values: {},
        labels: {},
        urlapi: "",
      },
    ];
    onAcoesChange(next);
    setExpandedIdx(next.length - 1);
  };

  const updateAction = (idx, patch) => {
    onAcoesChange((list) =>
      (list || []).map((item, i) => (i === idx ? mergeActionPatch(item, patch) : item))
    );
  };

  const replaceAction = (idx, patch) => {
    onAcoesChange((list) =>
      (list || []).map((item, i) => (i === idx ? { ...item, ...patch } : item))
    );
    setExpandedIdx(idx);
  };

  const moveAction = (idx, dir) => {
    const list = [...(acoes || [])];
    const next = idx + dir;
    if (next < 0 || next >= list.length) return;
    const tmp = list[idx];
    list[idx] = list[next];
    list[next] = tmp;
    onAcoesChange(list);
    setExpandedIdx(next);
  };

  const removeAction = (idx) => {
    onAcoesChange((list) => (list || []).filter((_, i) => i !== idx));
    setExpandedIdx((cur) => (cur === idx ? null : cur != null && cur > idx ? cur - 1 : cur));
  };

  const toggleExpand = (idx) => {
    setExpandedIdx((cur) => (cur === idx ? null : idx));
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary">
        As ações rodam em ordem, após a mensagem do passo. Use a busca para encontrar entre{" "}
        {actions.length > 0 ? `${actions.length} opções` : "as opções disponíveis"}.
      </Typography>

      {acoes.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            borderStyle: "dashed",
            bgcolor: "background.default",
          }}
        >
          <ActionSearchField
            actions={actions}
            isLoading={isLoading}
            placeholder="Buscar ação para adicionar…"
            onPick={addAction}
          />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Ex.: alterar solicitação, tag, comprar passagem…
            </Typography>
            <Button
              size="small"
              variant="text"
              sx={{ textTransform: "none", flexShrink: 0 }}
              onClick={() => setAddOpen(true)}
              disabled={isLoading || !actions.length}
            >
              Categorias
            </Button>
          </Box>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {(acoes || []).map((item, idx) => (
            <ActionRow
              key={`${item.value}-${idx}`}
              item={item}
              idx={idx}
              total={acoes.length}
              catalog={findCatalogAction(actions, item)}
              actions={actions}
              isLoading={isLoading}
              expanded={expandedIdx === idx}
              onToggleExpand={toggleExpand}
              onMove={moveAction}
              onRemove={removeAction}
              onReplace={replaceAction}
              onUpdate={updateAction}
              onNavigateToSlot={onNavigateToSlot}
              slots={slots}
            />
          ))}
        </Stack>
      )}

      {acoes.length > 0 ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleOutline />}
          onClick={() => setAddOpen(true)}
          disabled={isLoading || !actions.length}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Adicionar outra ação
        </Button>
      ) : null}

      <AddActionDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        actions={actions}
        isLoading={isLoading}
        onPick={addAction}
      />
    </Stack>
  );
}
